// src/services/pmt3DashboardDataService.ts
import { db } from '@/config/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

// --- Interfaces for Farming Basics Data ---
interface ProductData {
  'Days Since Farming'?: number;
  'Times Farmed'?: number;
  'Time Farming, if Actively Farming'?: number;
  'Final Farming Cost Per New CST'?: number;
  'Farming Group'?: number | string;
  'Share Terms in Farming Group'?: string;
  'Broad Seeds URL'?: string;
  'Broad Seeds Type'?: string;
}

interface FarmingHistoryData {
  'Lifetime Days Farming'?: number;
}

interface FarmingGroupData {
    id: string; // ASIN
    'Farming Group'?: number;
    'Product Name'?: string;
}

interface GroupAsinInfo {
    productName: string;
    asin: string;
}

interface OverrideLedgerData {
  'New Value'?: string | number;
  'Date'?: string; // mm/dd/yyyy format
  'date'?: string; // Longer string format with timezone
}

// --- Existing Functions ---

/**
 * Fetches the 'Qualifying Terms...' field from the pmt2_max_bid_adjustments collection.
 */
export const fetchMaxBidAdjustmentData = async (
    companyName: string,
    asin: string
): Promise<string | null> => {
    if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_max_bid_adjustments', companyName, 'entries', asin);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data()?.['Qualifying Terms That Would Really Like to Bid Higher'] ?? null;
        }
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Fetches the 'Days ago' field from the pmt2_last_update collection.
 */
export const fetchLastUpdateDaysAgo = async (
    companyName: string,
    asin: string
): Promise<number | null> => {
     if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_last_update', companyName, 'entries', asin);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const daysAgo = docSnap.data()?.['Days ago'];
            return typeof daysAgo === 'number' ? daysAgo : null;
        }
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Fetches JLS inventory data...
 */
export const fetchJlsInventoryData = async (
    companyName: string,
    asin: string
): Promise<{ potentialUnits: any, preProcessedUnits: any, inbound: any } | null> => {
    if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_jls_bundle_skus', companyName, 'entries', asin);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                potentialUnits: data?.['Potential Units'] ?? null,
                preProcessedUnits: data?.['Pre-Processed Units'] ?? null,
                inbound: data?.['Inbound'] ?? null
            };
        }
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Fetches average inventory data...
 */
export const fetchAverageInventoryData = async (
    companyName: string,
    asin: string
): Promise<{ avgTotalInventory: any, avgDaysOfCover: any } | null> => {
    if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_average_inventory_levels', companyName, 'entries', asin);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                avgTotalInventory: data?.average_28_day_total_inventory ?? null,
                avgDaysOfCover: data?.average_28_day_days_of_cover ?? null
            };
        }
        return null;
    } catch (error) {
        return null;
    }
};


// --- New Functions for Farming Basics ---

/**
 * Fetches the 'Farming Cost Per New CST Override' ledger entry data.
 */
export const fetchFarmingCostOverrideData = async (
    companyName: string,
    asin: string
): Promise<OverrideLedgerData | null> => {
    if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_ledger', companyName, asin, 'Farming Cost Per New CST Override');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            // Check if entries array exists and has data
            if (data?.entries && Array.isArray(data.entries) && data.entries.length > 0) {
                return data.entries[0] as OverrideLedgerData; // Return the first entry
            }
        }
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Fetches the main product data from pmt2_data.
 */
export const fetchProductData = async (
    companyName: string,
    asin: string
): Promise<ProductData | null> => {
     if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_data', companyName, 'products', asin);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? docSnap.data() as ProductData : null;
    } catch (error) {
        return null;
    }
};

/**
 * Fetches the farming history data for a specific ASIN.
 */
export const fetchFarmingHistoryData = async (
    companyName: string,
    asin: string
): Promise<FarmingHistoryData | null> => {
     if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_farming_history', companyName, 'entries', asin);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? docSnap.data() as FarmingHistoryData : null;
    } catch (error) {
        return null;
    }
};

/**
 * Fetches the farming group data for a specific ASIN.
 */
export const fetchFarmingGroupData = async (
    companyName: string,
    asin: string
): Promise<FarmingGroupData | null> => {
     if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_farming_groups', companyName, 'asins', asin);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
             return { id: docSnap.id, ...docSnap.data() } as FarmingGroupData;
        }
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Fetches all ASINs belonging to a specific farming group for a company.
 */
export const fetchGroupAsins = async (
    companyName: string,
    groupNumber: number | null | undefined
): Promise<GroupAsinInfo[]> => {
    if (!companyName || groupNumber === null || groupNumber === undefined || isNaN(groupNumber)) {
        return [];
    }
    try {
        const groupQuery = query(
            collection(db, 'pmt2_farming_groups', companyName, 'asins'),
            where('Farming Group', '==', groupNumber)
        );
        const groupSnap = await getDocs(groupQuery);
        const asins: GroupAsinInfo[] = groupSnap.docs.map(docSnap => {
            const data = docSnap.data() as FarmingGroupData;
            return {
                productName: data['Product Name'] ?? 'Unknown Product',
                asin: docSnap.id
            };
        });
        asins.sort((a, b) => a.productName.localeCompare(b.productName));
        return asins;
    } catch (error) {
        return [];
    }
};